import { computed, defineComponent, onMounted, onUnmounted, onUpdated, PropType, ref, watch } from 'vue';
import { Popover } from 'bootstrap';
import { useStore } from 'vuex';
import { useDateFormat } from '@/composables/dateFormat';
import { ProjectAction, ProjectGetter } from '@/store/project/project.module';
import { Project } from '@/store/project/project.model';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { Province } from '@/store/province/province.model';
import LoadingComponent from '@/components/loading/Loading.vue';
import ProjectOffcanvasComponent from '@/components/project/create/createTable/projectOffcanvas/ProjectOffcanvas.vue';
import ProjectAttachmentModalComponent from '@/components/project/detail/projectAttachmentModal/ProjectAttachmentModal.vue';
import ProjectAttachmentFormComponent from '@/components/project/detail/projectAttachmentForm/ProjectAttachmentForm.vue';
import ProjectImageModalComponent from '@/components/project/detail/projectImageModal/ProjectImageModal.vue';

export default defineComponent({
  name: 'ResidualTableComponent',
  components: {
    LoadingComponent,
    ProjectOffcanvasComponent,
    ProjectAttachmentModalComponent,
    ProjectAttachmentFormComponent,
    ProjectImageModalComponent,
  },
  props: {
    fiscalYear: {
      type: Object as PropType<FiscalYear>,
    },
    province: {
      type: Object as PropType<Province>,
    }
  },
  emits: [
    'toggle',
  ],
  setup: (props, context) => {
    const store = useStore();
    const dateFormat = useDateFormat();
    const projects = computed<Project[]>(() => store.getters[ProjectGetter.RESIDUAL]);
    const isLoading = computed(() => store.getters[ProjectGetter.STATUS] === 'init' || store.getters[ProjectGetter.STATUS] === 'loading');
    const datatable = ref<HTMLTableElement>();
    const dtInstance = ref();
    const popoverList = ref<Popover[]>([]);
    const projectOffcanvas = ref<InstanceType<typeof ProjectOffcanvasComponent>>();
    const selectedProjectId = ref<number>();
    const projectAttachmentModel = ref<InstanceType<typeof ProjectAttachmentModalComponent>>();
    const projectAttachmentForm = ref<InstanceType<typeof ProjectAttachmentFormComponent>>();
    const projectImageModal = ref<InstanceType<typeof ProjectImageModalComponent>>();
    const isUpload = ref(false);

    onMounted(() => {
      if (props.fiscalYear && props.province) {
        updateDataTable(props.fiscalYear.id, props.province.id);
      }
    });

    onUnmounted(() => {
      dtInstance.value?.destroy();
    });

    onUpdated(() => {
      Promise.resolve(initialPopover())
        .then(() => initialDataTable());
    });

    watch(() => ([props.fiscalYear, props.province]), ([fiscalYear, province]) => {
      if (fiscalYear && province) {
        updateDataTable(fiscalYear.id, province.id);
      }
    });

    const updateDataTable = (fiscalYearId: number, provinceId: number) => {
      Promise.resolve(dtInstance.value?.destroy())
        .then(() => {
          store.dispatch(ProjectAction.FETCH, {
            fiscalYearId: fiscalYearId,
            appointment: 'Provinces',
            appointmentId: provinceId
          })
            .then(() => initialDataTable());
        });
    };

    const initialDataTable = () => {
      const dt = datatable.value as HTMLTableElement;
      if (!$.fn.dataTable.isDataTable(dt)) {
        dtInstance.value = $(dt).DataTable({
          columnDefs: [
            { type: 'formatted-num', targets: 3 },
          ],
          language: {
            emptyTable: 'ไม่พบข้อมูล',
            info: 'แสดง _START_ ถึง _END_ จากจำนวนทั้งหมด _TOTAL_',
            infoEmpty: 'แสดง 0 ถึง 0 จากจำนวนทั้งหมด 0',
            infoFiltered: '(ค้นหาจากจำนวนทั้งหมด _MAX_)',
            lengthMenu: 'แสดงข้อมูล _MENU_',
            paginate: {
              next: 'ถัดไป',
              previous: 'ก่อนหน้า',
              first: 'First',
              last: 'Last'
            },
            search: 'ค้นหา:',
            zeroRecords: 'ไม่พบข้อมูลที่ค้นหา',
          }
        });
      }
    };

    // popover
    const initialPopover = () => {
      const popoverTriggerList = computed<HTMLElement[]>(() => [].slice.call(document.querySelectorAll('[data-bs-toggle="popover-project"]')) as HTMLElement[]);
      popoverList.value.map(element => element.hide());
      popoverList.value = popoverTriggerList.value?.map(element => new Popover(element, { trigger: 'hover', placement: 'right' }));
    };

    const createClick = () => {
      context.emit('toggle');
    };

    const selectedClick = (projectId: number) => {
      Promise.resolve(selectedProjectId.value = projectId)
        .then(() => projectOffcanvas.value?.show());
    };

    const attachmentClick = (projectId: number) => {
      isUpload.value = false;
      Promise.resolve(selectedProjectId.value = projectId)
        .then(() => projectOffcanvas.value?.hide())
        .then(() => projectAttachmentModel.value?.show());
    };

    const attachmentClose = (projectId: number) => {
      if (!isUpload.value) {
        Promise.resolve(selectedProjectId.value = projectId)
          .then(() => projectAttachmentModel.value?.hide())
          .then(() => projectOffcanvas.value?.show());
      }
    };

    const uploadClick = (projectId: number) => {
      isUpload.value = true;
      Promise.resolve(selectedProjectId.value = projectId)
        .then(() => projectAttachmentModel.value?.hide())
        .then(() => projectAttachmentForm.value?.show());
    };

    const uploadClose = (projectId: number) => {
      isUpload.value = false;
      Promise.resolve(selectedProjectId.value = projectId)
        .then(() => projectAttachmentForm.value?.hide())
        .then(() => projectAttachmentModel.value?.show());
    };

    const imageClick = (projectId: number) => {
      Promise.resolve(selectedProjectId.value = projectId)
        .then(() => projectImageModal.value?.show());
    };

    const thaiDate = (date: Date) =>
      dateFormat.toThaiDate(date, 'short');

    const status = (isCanceled: boolean, name: string, color: string) => {
      if (isCanceled) {
        return '<span class="text-danger">ยกเลิก</span>';
      }

      return `<span style="color: ${color}">${name}</span>`;
    };

    return {
      projects,
      datatable,
      isLoading,
      projectOffcanvas,
      selectedProjectId,
      projectAttachmentModel,
      projectAttachmentForm,
      projectImageModal,
      createClick,
      selectedClick,
      attachmentClick,
      attachmentClose,
      uploadClick,
      uploadClose,
      imageClick,
      thaiDate,
      status,
    };
  },
});
